import './HvacResultSavingsSystem.scss';
import { CurrencyCode, EnergySource, HvacArea, HvacResultsArea } from '@belimo-retrofit-portal/logic';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { CarbonSavingsUnit } from 'src/modules/common/components/CarbonSavingsUnit';
import { EnergyEfficiencyStack } from 'src/modules/common/components/EnergyEfficiencyStack';
import { EnergySavingsUnit } from 'src/modules/common/components/EnergySavingsUnit';
import { SavingsAmount } from 'src/modules/common/components/SavingsAmount';
import { SavingsValue } from 'src/modules/common/components/SavingsValue';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { HeatingIcon } from 'src/modules/common/icons/HeatingIcon';
import { LightningIcon } from 'src/modules/common/icons/LightningIcon';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { HvacResultSavingsCard } from 'src/modules/hvac/views/HvacResultSavingsCard';

type Props = {
  readonly currency: CurrencyCode;
  readonly results: HvacResultsArea;
};

export const HvacResultSavingsSystem = ({ currency, results }: Props): React.ReactElement => (
  <div className="bp-hvac-result-savings-system">
    <div className="bp-hvac-result-savings-system__assessments">
      <div className="bp-hvac-result-savings-system__assessment">
        <BodyMedium01>
          <FormattedMessage id="hvac/result/savings/efficiency/actual/title"/>
        </BodyMedium01>

        <div className="bp-hvac-result-savings-system__assessment-stack">
          <EnergyEfficiencyStack value={results.rating.actual}/>
        </div>
      </div>

      <div className="bp-hvac-result-savings-system__assessment">
        <BodyMedium01>
          <FormattedMessage id="hvac/result/savings/efficiency/future/title"/>
        </BodyMedium01>

        <div className="bp-hvac-result-savings-system__assessment-stack">
          <EnergyEfficiencyStack value={results.rating.future}/>
        </div>
      </div>
    </div>

    <div className="bp-hvac-result-savings-system__cards">
      {results.type !== HvacArea.VENTILATION && (
        <HvacResultSavingsCard
          icon={<HeatingIcon/>}
          title={<FormattedMessage id="hvac/result/savings/potential/thermalSavings/title"/>}
          label={<FormattedMessage id="hvac/result/savings/potential/thermalSavings/diagram/label"/>}
          value={(
            <>
              <SavingsValue
                value={results.savings.thermal.relative?.mul(100) ?? null}
                precision={0}
              />

              <FormattedMessage id="unit/percent"/>
            </>
          )}
          firstDetailLabel={(
            <FormattedMessage
              id="hvac/result/savings/potential/thermalSavings/energy"
              values={{ sub, sup }}
            />
          )}
          firstDetailValue={(
            <>
              <SavingsValue
                value={results.savings.thermal.absolute?.div(1_000) ?? null}
                precision={0}
              />

              <EnergySavingsUnit source={results.savings.thermal.source}/>

            </>
          )}
          secondDetailLabel={(
            <FormattedMessage
              id="hvac/result/savings/potential/thermalSavings/annual"
              values={{ sub, sup }}
            />
          )}
          secondDetailValue={(
            <SavingsAmount
              value={results.savings.thermal.annual}
              currency={currency}
            />
          )}
        />
      )}

      <HvacResultSavingsCard
        icon={<LightningIcon/>}
        title={<FormattedMessage id="hvac/result/savings/potential/electricalSavings/title"/>}
        label={<FormattedMessage id="hvac/result/savings/potential/electricalSavings/diagram/label"/>}
        value={(
          <><SavingsValue
            value={results.savings.electrical.relative?.mul(100) ?? null}
            precision={0}
          /><FormattedMessage id="unit/percent"/>
          </>
        )}
        firstDetailLabel={(
          <FormattedMessage
            id="hvac/result/savings/potential/electricalSavings/energy"
            values={{ sub, sup }}
          />
        )}
        firstDetailValue={(
          <><SavingsValue
            value={results.savings.electrical.absolute?.div(1_000) ?? null}
            precision={0}
          /><EnergySavingsUnit source={EnergySource.ELECTRICITY}/>
          </>
        )}
        secondDetailLabel={(
          <FormattedMessage
            id="hvac/result/savings/potential/electricalSavings/annual"
            values={{ sub, sup }}
          />
        )}
        secondDetailValue={(
          <SavingsAmount
            value={results.savings.electrical.annual}
            currency={currency}
          />
        )}
      />

      <HvacResultSavingsCard
        icon={<LightningIcon/>}
        title={(
          <FormattedMessage
            id="hvac/result/savings/potential/carbonDioxide/title"
            values={{ sub, sup }}
          />
        )}
        label={(
          <FormattedMessage
            id="hvac/result/savings/potential/carbonDioxide/diagram/label"
            values={{ sub, sup }}
          />
        )}
        value={(
          <>
            <SavingsValue
              value={results.savings.carbon.relative?.mul(100) ?? null}
              precision={0}
            />

            <FormattedMessage id="unit/percent"/>
          </>
        )}
        firstDetailLabel={results.type !== HvacArea.VENTILATION && (
          <FormattedMessage
            id="hvac/result/savings/potential/carbonDioxide/thermal"
            values={{ sub, sup }}
          />
        )}
        firstDetailValue={results.type !== HvacArea.VENTILATION && (
          <>
            <SavingsValue
              value={results.savings.carbon.thermal?.div(1_000_000) ?? null}
              precision={0}
            />

            <CarbonSavingsUnit/>
          </>
        )}
        secondDetailLabel={(
          <FormattedMessage
            id="hvac/result/savings/potential/carbonDioxide/electrical"
            values={{ sub, sup }}
          />
        )}
        secondDetailValue={(
          <>
            <SavingsValue
              value={results.savings.carbon.electrical?.div(1_000_000) ?? null}
              precision={0}
            />

            <CarbonSavingsUnit/>
          </>
        )}
      />
    </div>
  </div>
);
